import React from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { Logo, LogoProps } from '../Logo';
import { useTheme } from '../../../../../theming/useTheme';
import { SecondaryLinks } from './SecondaryLinks';
import { secondaryHeaderHeight } from '../header.utils';
import { ErrorBoundary } from '../../../../errorHandling/ErrorBoundary';
import { HUGE_SCREEN_SIZE, MEDIA_BREAKPOINTS } from '../../../../../mediaQueries.const';
import { createStyleForBoxShadow } from '../../../../../theming/boxShadow';
import { BaseUnit } from '../../../../../theming/baseUnitDefinition';
import { TranslatedFontSize } from '../../../../../theming/fontSizeTranslator';
import { AnimationDirection, AnimationDirectionType } from '../menuAnimations.util';
import { NavigationLink } from '../primaryHeader/PrimaryHeader';

const componentConfigThemeKey = 'secondaryHeader';

export const maxVisibleLinksOnLargeAndXLarge = 6;

export interface SecondaryHeaderDataProps {
  links: NavigationLink[] | null;
  logo: LogoProps | null;
  moreLinksText: string;
  pagePath: string;
  animation?: AnimationDirectionType;
  animationDuration?: number;
}

export interface SecondaryHeaderThemeProps {
  baseUnit: BaseUnit;
  backgroundColor: string;
  boxShadowTypeNormal: string;
  fontSizesStyles: TranslatedFontSize;
}

const getStyle = (baseUnit: BaseUnit, backgroundColor: string, animationDuration: number) => {
  return StyleSheet.create({
    headerWrapper: {
      backgroundColor,
      zIndex: 50,
      position: 'relative',
    },
    header: {
      height: `${secondaryHeaderHeight}px`,
      display: 'grid',
      gridAutoFlow: 'column',
      width: '100%',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gridColumnGap: `${baseUnit}px`,
      paddingLeft: `${baseUnit}px`,
      maxWidth: `${HUGE_SCREEN_SIZE}px`,
      margin: '0 auto',
      boxSizing: 'border-box',
    },
    foldingUp: {
      transition: `${animationDuration}ms`,
      [MEDIA_BREAKPOINTS.medium]: {
        transform: 'translateY(-100%)',
      },
      [MEDIA_BREAKPOINTS.small]: {
        transform: 'translateY(-100%)',
      },
    },
    foldingDown: {
      transition: `${animationDuration}ms`,
      [MEDIA_BREAKPOINTS.medium]: {
        transform: 'translateY(0)',
        transitionDelay: '200ms',
        transition: `${animationDuration}ms`,
      },
      [MEDIA_BREAKPOINTS.small]: {
        transform: 'translateY(0)',
        transitionDelay: '200ms',
      },
    },
  });
};

type SecondaryHeaderStyle = {
  headerWrapper: object;
  header: object;
  foldingUp: object;
  foldingDown: object;
};

const getClassWithAnimations = (animation: string, baseStyle: SecondaryHeaderStyle, boxShadow: { style: object }) => {
  switch (animation) {
    case AnimationDirection.UP: return css(baseStyle.headerWrapper, boxShadow.style, baseStyle.foldingUp);
    case AnimationDirection.DOWN: return css(baseStyle.headerWrapper, boxShadow.style, baseStyle.foldingDown);
    default: return css(baseStyle.headerWrapper, boxShadow.style);
  }
};

export const SecondaryHeader: React.FunctionComponent<SecondaryHeaderDataProps> = props => {
  const { links, logo, moreLinksText, pagePath, animationDuration = 0, animation = AnimationDirection.NONE } = props;
  const { baseUnit, backgroundColor, boxShadowTypeNormal } = useTheme<SecondaryHeaderThemeProps>(componentConfigThemeKey);
  const style = getStyle(baseUnit, backgroundColor, animationDuration);
  const boxShadowStyle = createStyleForBoxShadow({ boxShadowTypeNormal });
  const combinedStyle = getClassWithAnimations(animation, style, boxShadowStyle);
  return (
    <ErrorBoundary>
      <div className={combinedStyle}>
        <div className={css(style.header)}>
          {logo ? <Logo {...logo} /> : null}
          {links ? <SecondaryLinks links={links} maxVisibleLinks={maxVisibleLinksOnLargeAndXLarge} moreLinksText={moreLinksText} pagePath={pagePath} /> : null}
        </div>
      </div>
    </ErrorBoundary>
  );
};
